/**
 * wsg-brand.js — UX glue for Wholesale Gorilla signup pages.
 *
 * (1) Inline per-field validation:
 *     Suppresses the browser's native validation popup (which shows only
 *     one error at a time) and instead marks every invalid field with a
 *     red border and a contextual inline message below it, so the user
 *     can see all problems at once and knows which fields to fix.
 *
 * (2) Preserve form values across WSG's error redirect:
 *     When WSG's server rejects a submission it redirects back with
 *     ?error=<code> and an empty form — wiping everything the user typed.
 *     We snapshot the form to sessionStorage on submit, restore it on the
 *     bounce-back page, and clear the snapshot once the account is
 *     successfully created. File inputs cannot be programmatically
 *     re-populated; we surface a notice asking the user to re-attach.
 *
 * (3) ?error=… banner + field highlight:
 *     We map known WSG error codes to a specific field where possible
 *     ('duplicate'/'email' → email input), highlight that field, and
 *     keep the banner for context. The URL param is stripped so a
 *     refresh doesn't re-show it.
 *
 * (4) International phone input (intl-tel-input):
 *     Wraps #phone with a flag/country-code picker, format-as-you-type,
 *     and isValidNumber() for real per-country validation. The picker
 *     auto-syncs both ways with the form's Country dropdown (pick US
 *     above → flag flips to US; flip flag to UK → country dropdown
 *     jumps to United Kingdom). On submit, the phone field's value is
 *     normalized to E.164 (e.g. +14155552671) before WSG reads it.
 *     The library loads from jsDelivr on demand and falls back to the
 *     plain regex check if the CDN is blocked.
 */
(function () {
  'use strict';

  var FORM_SELECTOR = '#wsg-signup';
  var STORAGE_KEY = 'wsg-signup-snapshot';
  var SNAPSHOT_FILE_FLAG = '__hadFile';

  // Populated by initPhoneInput() once intl-tel-input loads. Null until
  // then (and stays null if the CDN is blocked) — every consumer must
  // null-check it and fall back to the plain regex validator.
  var wsgIti = null;

  // session_ref is a WSG anti-spam token populated server-side per page load;
  // restoring a stale value would be rejected, so never round-trip it.
  var EXCLUDED_FIELD_NAMES = { session_ref: true };

  var ERROR_HIGHLIGHT_FIELDS = {
    duplicate: ['email'],
    account_exists: ['email'],
    email: ['email'],
    phone: ['phone'],
    address: ['wsg-form-address1'],
    address1: ['wsg-form-address1'],
    city: ['wsg-form-city'],
    country: ['wsg-form-country'],
    province: ['wsg-form-province'],
    state: ['wsg-form-province'],
    zip: ['wsg-form-zip'],
    postal_code: ['wsg-form-zip'],
    first_name: ['first_name'],
    last_name: ['last_name'],
    company: ['company'],
    terms: ['terms'],
    vat: ['vatNumber'],
    vat_number: ['vatNumber'],
    tax_id: ['vatNumber']
  };

  var ERROR_FIELD_MESSAGES = {
    duplicate: 'An account with this email already exists. Try logging in instead.',
    account_exists: 'An account with this email already exists. Try logging in instead.',
    email: 'Please enter a valid email address.',
    phone: 'Please enter a valid phone number.',
    address: 'Please enter a valid street address.',
    address1: 'Please enter a valid street address.',
    city: 'Please enter a valid city.',
    country: 'Please select a country.',
    province: 'Please select a state or province.',
    state: 'Please select a state or province.',
    zip: 'Please enter a valid zip / postal code.',
    postal_code: 'Please enter a valid zip / postal code.',
    first_name: 'Please enter your first name.',
    last_name: 'Please enter your last name.',
    company: 'Please enter your company name.',
    terms: 'You must accept the terms and conditions to continue.',
    vat: 'Please enter a valid Tax ID / VAT number.',
    vat_number: 'Please enter a valid Tax ID / VAT number.',
    tax_id: 'Please enter a valid Tax ID / VAT number.'
  };

  // Fields that get always-on live validation (as the user types / changes).
  // Other fields are only validated on submit and auto-cleared when fixed.
  var LIVE_VALIDATE_IDS = ['phone', 'wsg-form-country', 'wsg-form-province'];

  // Per-field custom validators. Return `true` if valid, or an error string.
  // Called *after* the field passes native HTML5 validity.
  var CUSTOM_VALIDATORS = {
    // Prefer intl-tel-input's per-country isValidNumber() when available
    // (knows that "+447911 123456" is a valid UK mobile, etc.). Falls
    // back to a digit-count heuristic when the CDN is blocked or the
    // utils.js hasn't loaded yet.
    phone: function (value) {
      if (!value) return true; // empty handled by native `required`
      if (wsgIti && typeof wsgIti.isValidNumber === 'function') {
        // utils.js may not have loaded yet — isValidNumber returns null in that case.
        var iv = wsgIti.isValidNumber();
        if (iv === true) return true;
        if (iv === false) {
          var data = wsgIti.getSelectedCountryData && wsgIti.getSelectedCountryData();
          var country = (data && data.name) ? ' for ' + data.name : '';
          return 'Please enter a valid phone number' + country + '.';
        }
        // null → fall through to heuristic
      }
      var trimmed = value.trim();
      if (!/^[+]?[\d\s().\-\/]+$/.test(trimmed)) {
        return 'Phone number can only contain digits, spaces, dashes, parentheses, and an optional leading +.';
      }
      var digits = trimmed.replace(/\D/g, '');
      if (digits.length < 7) return 'Phone number is too short — please enter at least 7 digits.';
      if (digits.length > 15) return 'Phone number is too long — please enter at most 15 digits.';
      return true;
    }
  };

  var ERROR_BANNER_MESSAGES = {
    general:
      'We could not create your account. Please double-check the form below and try again.',
    validation:
      'Some fields are missing or contain invalid values. Please review and resubmit.',
    duplicate:
      'An account with this email already exists. Try logging in instead.',
    account_exists:
      'An account with this email already exists. Try logging in instead.',
    email: 'Please enter a valid email address and try again.',
    phone: 'Please enter a valid phone number and try again.',
    address: 'Please check the address you entered and try again.',
    address1: 'Please check the address you entered and try again.',
    city: 'Please check the city you entered and try again.',
    country: 'Please select a country and try again.',
    province: 'Please select a state / province and try again.',
    state: 'Please select a state / province and try again.',
    zip: 'Please check the zip / postal code you entered and try again.',
    postal_code: 'Please check the zip / postal code you entered and try again.',
    first_name: 'Please enter your first name and try again.',
    last_name: 'Please enter your last name and try again.',
    company: 'Please enter your company name and try again.',
    terms: 'You must accept the terms and conditions to continue.',
    vat: 'Please enter a valid Tax ID / VAT number and try again.',
    vat_number: 'Please enter a valid Tax ID / VAT number and try again.',
    tax_id: 'Please enter a valid Tax ID / VAT number and try again.',
    captcha: 'Verification failed. Please try the submission again.',
    required: 'Please fill out all required fields and resubmit.'
  };

  /* ─── helpers ───────────────────────────────────────────────────────── */

  function getForm() {
    return document.querySelector(FORM_SELECTOR);
  }

  function isSnapshotableField(field) {
    if (!field || !field.name) return false;
    if (EXCLUDED_FIELD_NAMES[field.name]) return false;
    var t = field.type;
    return t !== 'file' && t !== 'password' && t !== 'hidden' && t !== 'submit' && t !== 'button';
  }

  function fieldGroup(field) {
    if (!field) return null;
    return (
      (field.closest && field.closest('.wsg-input-small, .grid__item, .full-width-block')) ||
      field.parentNode
    );
  }

  function fieldLabelText(field) {
    if (!field || !field.id) return 'This field';
    var labelEl = document.querySelector(
      'label[for="' + field.id + '"]:not(.wsg-hidden-label):not(.wsg-label-error)'
    );
    if (labelEl && labelEl.textContent) {
      return labelEl.textContent.trim().replace(/\s+/g, ' ');
    }
    return field.placeholder || 'This field';
  }

  function validationMessageFor(field) {
    if (!field) return 'This field is required.';
    var v = field.validity;

    if (field.type === 'checkbox' && v && v.valueMissing) {
      if (field.id === 'terms') return 'You must accept the terms and conditions to continue.';
      if (field.id === 'marketing_opt_in') return 'Please opt in to continue.';
      return 'This box must be checked to continue.';
    }

    if (v) {
      if (v.valueMissing) return fieldLabelText(field) + ' is required.';
      if (v.typeMismatch) {
        if (field.type === 'email') return 'Please enter a valid email address.';
        if (field.type === 'tel') return 'Please enter a valid phone number.';
        if (field.type === 'url') return 'Please enter a valid URL.';
      }
      if (v.patternMismatch) return fieldLabelText(field) + ' is not in the expected format.';
      if (v.tooShort) return fieldLabelText(field) + ' is too short.';
      if (v.tooLong) return fieldLabelText(field) + ' is too long.';
    }
    return field.validationMessage || 'Please correct this field.';
  }

  function showFieldError(field, message) {
    if (!field) return;
    field.classList.add('error-border');
    field.setAttribute('aria-invalid', 'true');

    var group = fieldGroup(field);
    if (!group) return;

    var existing = group.querySelector('.wsg-inline-error[data-for="' + field.id + '"]');
    if (existing) existing.remove();

    var err = document.createElement('div');
    err.className = 'wsg-inline-error';
    err.setAttribute('data-for', field.id);
    err.setAttribute('role', 'alert');
    err.textContent = message;
    group.appendChild(err);
  }

  function clearFieldError(field) {
    if (!field) return;
    field.classList.remove('error-border');
    field.removeAttribute('aria-invalid');
    var group = fieldGroup(field);
    if (!group) return;
    var existing = group.querySelector('.wsg-inline-error[data-for="' + field.id + '"]');
    if (existing) existing.remove();
  }

  function clearAllFieldErrors(form) {
    if (!form) return;
    var errs = form.querySelectorAll('.wsg-inline-error');
    for (var i = 0; i < errs.length; i++) errs[i].remove();
    var marked = form.querySelectorAll('.error-border');
    for (var j = 0; j < marked.length; j++) {
      marked[j].classList.remove('error-border');
      marked[j].removeAttribute('aria-invalid');
    }
  }

  function isLiveValidated(field) {
    return !!(field && field.id && LIVE_VALIDATE_IDS.indexOf(field.id) !== -1);
  }

  // Validate a single field "live" (on input / change / blur).
  // Suppresses the noisy "Required" message until the field has been
  // touched (blurred) at least once, so we don't yell at a fresh page.
  function liveValidateField(field) {
    if (!field) return;
    var nativeOk = typeof field.checkValidity !== 'function' || field.checkValidity();
    if (!nativeOk) {
      var v = field.validity;
      var touched = field.dataset && field.dataset.wsgTouched === '1';
      var hasValue = field.value && field.value.length > 0;
      if (v && v.valueMissing && !touched && !hasValue) {
        clearFieldError(field);
        return;
      }
      showFieldError(field, validationMessageFor(field));
      return;
    }
    var custom = CUSTOM_VALIDATORS[field.id];
    if (custom) {
      var result = custom(field.value, field);
      if (result !== true) {
        showFieldError(field, typeof result === 'string' ? result : 'Please correct this field.');
        return;
      }
    }
    clearFieldError(field);
  }

  function runClientValidation(form) {
    if (!form) return true;
    clearAllFieldErrors(form);

    var firstInvalid = null;
    var fields = form.querySelectorAll('input, select, textarea');
    for (var i = 0; i < fields.length; i++) {
      var f = fields[i];
      if (f.disabled || f.type === 'hidden') continue;

      var nativeOk = typeof f.checkValidity !== 'function' || f.checkValidity();
      if (!nativeOk) {
        showFieldError(f, validationMessageFor(f));
        if (!firstInvalid) firstInvalid = f;
        continue;
      }
      // Even if native validity passes, check custom validators (e.g. phone format)
      var custom = CUSTOM_VALIDATORS[f.id];
      if (custom) {
        var result = custom(f.value, f);
        if (result !== true) {
          showFieldError(f, typeof result === 'string' ? result : 'Please correct this field.');
          if (!firstInvalid) firstInvalid = f;
        }
      }
    }

    if (firstInvalid) {
      try {
        firstInvalid.focus({ preventScroll: true });
        firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' });
      } catch (e) {
        firstInvalid.focus();
      }
      return false;
    }
    return true;
  }

  /* ─── (1) Submit-click validation, inline (no native popup) ─────────── */

  document.addEventListener(
    'click',
    function (e) {
      var form = getForm();
      if (!form) return;
      var submit =
        e.target && e.target.closest
          ? e.target.closest(
              'input[type="submit"], button[type="submit"], #wsg-submit, .wsg-submit-copy'
            )
          : null;
      if (!submit || !form.contains(submit)) return;

      if (!runClientValidation(form)) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      // Valid — normalize phone to E.164 so WSG receives a clean number,
      // then snapshot so we can restore values if WSG redirects with ?error=
      normalizePhoneToE164(form);
      snapshotForm(form);
    },
    true /* capture */
  );

  // Catch Enter-key submissions (which bypass the button click) and any
  // programmatic form.submit() WSG might fire.
  document.addEventListener(
    'submit',
    function (e) {
      var form = e.target;
      if (!form || form.id !== 'wsg-signup') return;
      if (!runClientValidation(form)) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }
      normalizePhoneToE164(form);
      snapshotForm(form);
    },
    true /* capture */
  );

  // Live validation handlers.
  //
  // - Phone (`#phone`): re-validates on every keystroke + on blur, so the
  //   user sees format problems immediately (the field that WSG just
  //   rejected with ?error=phone). Empty-but-required only surfaces after
  //   first blur, so we don't yell at a freshly-loaded page.
  // - Country (`#wsg-form-country`) and Province (`#wsg-form-province`):
  //   re-validate on change. When country changes, WSG repopulates the
  //   province options — we wait a tick and re-validate province too, so
  //   a now-orphaned province value gets cleared instead of staying
  //   stuck in an inconsistent state.
  // - Every other field: just clears its error when the user types a
  //   valid value (no live error display until submit).

  function handleInput(e) {
    var t = e.target;
    if (!t || !t.id) return;
    var form = getForm();
    if (!form || !form.contains(t)) return;

    if (isLiveValidated(t)) {
      liveValidateField(t);
      return;
    }
    if (t.classList.contains('error-border')) {
      if (typeof t.checkValidity === 'function' && t.checkValidity()) {
        clearFieldError(t);
      }
    }
  }

  function handleChange(e) {
    var t = e.target;
    if (!t || !t.id) return;
    var form = getForm();
    if (!form || !form.contains(t)) return;

    if (isLiveValidated(t)) {
      if (t.dataset) t.dataset.wsgTouched = '1';
      liveValidateField(t);
      // Country → re-validate province after WSG repopulates its options.
      if (t.id === 'wsg-form-country') {
        var prov = form.querySelector('#wsg-form-province');
        if (prov) {
          setTimeout(function () { liveValidateField(prov); }, 60);
        }
      }
      return;
    }
    if (t.classList.contains('error-border')) {
      if (typeof t.checkValidity === 'function' && t.checkValidity()) {
        clearFieldError(t);
      }
    }
  }

  function handleBlur(e) {
    var t = e.target;
    if (!t || !t.id) return;
    var form = getForm();
    if (!form || !form.contains(t)) return;
    if (!isLiveValidated(t)) return;
    if (t.dataset) t.dataset.wsgTouched = '1';
    liveValidateField(t);
  }

  document.addEventListener('input', handleInput, true);
  document.addEventListener('change', handleChange, true);
  document.addEventListener('blur', handleBlur, true);

  /* ─── (2) Snapshot / restore form across error redirect ─────────────── */

  function snapshotForm(form) {
    try {
      var data = {};
      var fields = form.querySelectorAll('input, select, textarea');
      for (var i = 0; i < fields.length; i++) {
        var f = fields[i];
        if (f.type === 'file') {
          if (f.files && f.files.length > 0) data[SNAPSHOT_FILE_FLAG] = true;
          continue;
        }
        if (!isSnapshotableField(f)) continue;
        if (f.type === 'checkbox' || f.type === 'radio') {
          data['__cb::' + f.name + '::' + f.value] = !!f.checked;
        } else {
          data[f.name] = f.value;
        }
      }
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(data));
    } catch (e) {
      /* sessionStorage can throw in private mode — silent fail is fine */
    }
  }

  function restoreForm(form) {
    var data;
    try {
      var raw = sessionStorage.getItem(STORAGE_KEY);
      if (!raw) return;
      data = JSON.parse(raw);
    } catch (e) {
      return;
    }
    if (!data || typeof data !== 'object') return;

    var savedProvince = null;
    var fields = form.querySelectorAll('input, select, textarea');

    for (var i = 0; i < fields.length; i++) {
      var f = fields[i];
      if (!isSnapshotableField(f)) continue;

      if (f.type === 'checkbox' || f.type === 'radio') {
        var key = '__cb::' + f.name + '::' + f.value;
        if (key in data) {
          f.checked = !!data[key];
          // Keep WSG's value-attribute mirror in sync for terms / marketing_opt_in
          f.setAttribute('value', f.checked ? 'true' : '');
        }
        continue;
      }

      // Defer province until the country-driven repopulation has happened.
      if (f.name === 'province') {
        if ('province' in data) savedProvince = data.province;
        continue;
      }

      if (f.name in data) {
        f.value = data[f.name];
        if (f.tagName === 'SELECT' && f.name === 'country') {
          f.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }

    if (savedProvince) {
      var provinceSel = form.querySelector('#wsg-form-province');
      if (provinceSel) {
        var trySet = function () {
          var opts = provinceSel.options;
          for (var j = 0; j < opts.length; j++) {
            if (opts[j].value === savedProvince) {
              provinceSel.value = savedProvince;
              return true;
            }
          }
          return false;
        };
        if (!trySet()) {
          var obs = new MutationObserver(function () {
            if (trySet()) obs.disconnect();
          });
          obs.observe(provinceSel, { childList: true });
          // Safety net — stop observing after 5s so we don't leak.
          setTimeout(function () { obs.disconnect(); }, 5000);
        }
      }
    }

    // File inputs can't be repopulated; flag it for the user.
    if (data[SNAPSHOT_FILE_FLAG]) {
      var fileLabel = document.getElementById('file_label');
      if (fileLabel) {
        fileLabel.textContent = 'Please re-attach your file — uploads aren’t preserved across errors.';
        fileLabel.style.color = '#c0392b';
      }
    }
  }

  function clearSnapshot() {
    try { sessionStorage.removeItem(STORAGE_KEY); } catch (e) { /* no-op */ }
  }

  /* ─── (3) URL ?error=… banner + field highlight ─────────────────────── */

  function showErrorBanner() {
    var params;
    try {
      params = new URLSearchParams(window.location.search);
    } catch (e) {
      return;
    }

    var errorCode = params.get('error');

    if (!errorCode) {
      // Landed on the post-signup success page → done with the snapshot.
      if (document.querySelector('#wsg-account-create, #wsg-account-container')) {
        clearSnapshot();
      }
      return;
    }
    if (document.querySelector('.wsg-error-banner')) return;

    var host = document.querySelector(
      '#wsg-account-create, #wsg-proxy-content, #wsg-signup-container, .wsg-proxy-container'
    );
    if (!host || !host.parentNode) return;

    var message = ERROR_BANNER_MESSAGES[errorCode] || ERROR_BANNER_MESSAGES.general;

    var banner = document.createElement('div');
    banner.className = 'wsg-error-banner';
    banner.setAttribute('role', 'alert');

    var icon = document.createElement('span');
    icon.className = 'wsg-error-banner__icon';
    icon.setAttribute('aria-hidden', 'true');
    icon.textContent = '!';
    banner.appendChild(icon);

    var msg = document.createElement('span');
    msg.className = 'wsg-error-banner__msg';
    msg.textContent = message;
    banner.appendChild(msg);

    var closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'wsg-error-banner__close';
    closeBtn.setAttribute('aria-label', 'Dismiss');
    closeBtn.textContent = '×';
    closeBtn.addEventListener('click', function () { banner.remove(); });
    banner.appendChild(closeBtn);

    host.parentNode.insertBefore(banner, host);

    // Restore typed values and highlight the offending field where known.
    var form = getForm();
    if (form) {
      restoreForm(form);

      var highlightIds = ERROR_HIGHLIGHT_FIELDS[errorCode] || [];
      var fieldMsg = ERROR_FIELD_MESSAGES[errorCode];
      if (fieldMsg && highlightIds.length) {
        for (var i = 0; i < highlightIds.length; i++) {
          var f = form.querySelector('#' + highlightIds[i]);
          if (f) showFieldError(f, fieldMsg);
        }
      }
    }

    // Strip ?error=… from the URL so a reload doesn't keep showing it.
    try {
      params.delete('error');
      var qs = params.toString();
      var newUrl =
        window.location.pathname +
        (qs ? '?' + qs : '') +
        window.location.hash;
      window.history.replaceState({}, '', newUrl);
    } catch (e) {
      /* no-op */
    }
  }

  /* ─── (4) intl-tel-input integration for the phone field ─────────────── */

  // Pinned to a stable v18 release. v18 exposes the simple `utilsScript`
  // option that auto-loads format/validation utilities. Bump deliberately;
  // the API has changed in v23+.
  var INTL_TEL_VERSION = '18.2.1';
  var INTL_TEL_BASE =
    'https://cdn.jsdelivr.net/npm/intl-tel-input@' + INTL_TEL_VERSION + '/build';

  // Built-in country-name aliases for cases where Shopify and intl-tel-input
  // disagree on the spelling. Lowercase Shopify name → iso2.
  var COUNTRY_ALIASES = {
    'united states': 'us',
    'united kingdom': 'gb',
    'south korea': 'kr',
    'north korea': 'kp',
    'czech republic': 'cz',
    'czechia': 'cz',
    'russia': 'ru',
    'russian federation': 'ru',
    'vietnam': 'vn',
    'viet nam': 'vn',
    'taiwan': 'tw',
    'iran': 'ir',
    'syria': 'sy',
    'bolivia': 'bo',
    'venezuela': 've',
    'moldova': 'md',
    'macedonia': 'mk',
    'palestine': 'ps',
    'tanzania': 'tz',
    'laos': 'la',
    'macau': 'mo',
    'hong kong': 'hk',
    'côte d’ivoire': 'ci',
    "cote d'ivoire": 'ci',
    'cape verde': 'cv',
    'east timor': 'tl'
  };

  function ensureIntlTelInput() {
    if (window.intlTelInput) return Promise.resolve(window.intlTelInput);
    if (window.__wsgItiLoading) return window.__wsgItiLoading;

    window.__wsgItiLoading = new Promise(function (resolve, reject) {
      // CSS
      if (!document.querySelector('link[data-iti-css="1"]')) {
        var link = document.createElement('link');
        link.rel = 'stylesheet';
        link.href = INTL_TEL_BASE + '/css/intlTelInput.min.css';
        link.setAttribute('data-iti-css', '1');
        document.head.appendChild(link);
      }
      // JS
      var existing = document.querySelector('script[data-iti-js="1"]');
      if (existing) {
        existing.addEventListener('load', function () { resolve(window.intlTelInput); });
        existing.addEventListener('error', reject);
        return;
      }
      var script = document.createElement('script');
      script.src = INTL_TEL_BASE + '/js/intlTelInput.min.js';
      script.async = true;
      script.setAttribute('data-iti-js', '1');
      script.onload = function () { resolve(window.intlTelInput); };
      script.onerror = function () { reject(new Error('intl-tel-input failed to load')); };
      document.head.appendChild(script);
    });
    return window.__wsgItiLoading;
  }

  function guessIsoFromCountryName(name) {
    if (!name) return null;
    if (!window.intlTelInputGlobals || !window.intlTelInputGlobals.getCountryData) return null;
    var countries = window.intlTelInputGlobals.getCountryData();
    var lc = String(name).toLowerCase().trim();
    var core = lc.replace(/\s*\(.*?\)\s*/g, '').trim();

    for (var i = 0; i < countries.length; i++) {
      if (countries[i].name.toLowerCase() === lc) return countries[i].iso2;
    }
    for (var j = 0; j < countries.length; j++) {
      var cn = countries[j].name.toLowerCase().replace(/\s*\(.*?\)\s*/g, '').trim();
      if (cn === core) return countries[j].iso2;
    }
    if (COUNTRY_ALIASES[core]) return COUNTRY_ALIASES[core];
    return null;
  }

  function findCountryOptionByName(countrySelect, name) {
    if (!countrySelect || !name) return null;
    var lc = String(name).toLowerCase().trim();
    var core = lc.replace(/\s*\(.*?\)\s*/g, '').trim();
    var opts = countrySelect.options;
    for (var i = 0; i < opts.length; i++) {
      var opt = opts[i];
      var ov = (opt.value || '').toLowerCase();
      var ot = (opt.text || '').toLowerCase();
      if (ov === lc || ov === core || ot === lc || ot === core) return opt;
    }
    return null;
  }

  function initPhoneInput() {
    var form = getForm();
    if (!form) return;
    var phone = form.querySelector('#phone');
    if (!phone || phone.dataset.wsgItiInit === '1') return;
    phone.dataset.wsgItiInit = '1';

    ensureIntlTelInput()
      .then(function (intlTelInput) {
        if (!intlTelInput) return;

        var countrySelect = form.querySelector('#wsg-form-country');
        var initialIso =
          guessIsoFromCountryName(countrySelect && countrySelect.value) || 'us';

        wsgIti = intlTelInput(phone, {
          separateDialCode: true,
          initialCountry: initialIso,
          preferredCountries: ['us', 'ca', 'gb', 'au'],
          autoPlaceholder: 'aggressive',
          utilsScript: INTL_TEL_BASE + '/js/utils.js'
        });

        // Re-run live validation any time the country changes (validity
        // depends on the country, e.g. UK numbers vs US numbers).
        phone.addEventListener('countrychange', function () {
          liveValidateField(phone);
        });

        if (!countrySelect) return;

        // Form country → phone picker
        countrySelect.addEventListener('change', function () {
          if (!wsgIti) return;
          var iso = guessIsoFromCountryName(countrySelect.value);
          if (!iso) return;
          var current = wsgIti.getSelectedCountryData();
          if (current && current.iso2 === iso) return;
          wsgIti.setCountry(iso);
        });

        // Phone picker → form country
        phone.addEventListener('countrychange', function () {
          if (!wsgIti) return;
          var data = wsgIti.getSelectedCountryData();
          if (!data || !data.name) return;
          var opt = findCountryOptionByName(countrySelect, data.name);
          if (opt && countrySelect.value !== opt.value) {
            countrySelect.value = opt.value;
            countrySelect.dispatchEvent(new Event('change', { bubbles: true }));
          }
        });
      })
      .catch(function () {
        // CDN blocked / offline → fall back silently to the plain regex check.
      });
  }

  // Normalize the phone field to E.164 right before WSG reads the form.
  function normalizePhoneToE164(form) {
    if (!wsgIti || !form) return;
    var phone = form.querySelector('#phone');
    if (!phone || !phone.value) return;
    try {
      if (typeof wsgIti.isValidNumber === 'function' && !wsgIti.isValidNumber()) return;
      var e164 = wsgIti.getNumber(); // returns '' if unparseable
      if (e164) phone.value = e164;
    } catch (e) {
      /* ignore — leave whatever the user typed */
    }
  }

  if (document.readyState !== 'loading') {
    showErrorBanner();
    initPhoneInput();
  } else {
    document.addEventListener('DOMContentLoaded', function () {
      showErrorBanner();
      initPhoneInput();
    });
  }
})();
